// What the search box and /search share (module 10 U227; pipeline F3, B1953): the words, the timings, which store is
// asked for which kind, how two stores' answers are merged, and where each result opens.
//
// With Personal on, each store is asked and the answers are merged here (D12, D43: the stores are never joined in the
// database). The personal store is asked only for what a page can open from it: tasks (the Tasks page lists both
// stores), people (People lists both, and a person's page takes the store: BUGS.md D74) and what Cobrain read (the item
// page takes the store). Companies, deals, meetings and chats are the commercial store's pages, which read that store
// alone, so a personal row there would open on a page that cannot find it. Its hits are marked private and drawn in
// violet (U3).
import type { Store } from '../api.js';
import { formatDate } from '../labels.js';
import { search, SEARCH_KINDS, type NotYetReadPassage, type Passage, type SearchGroup, type SearchHit, type SearchKind } from './api.js';

export const KIND_LABEL: Record<SearchKind, string> = {
  companies: 'Companies', people: 'People', deals: 'Deals', meetings: 'Meetings', tasks: 'Tasks', chats: 'Chats',
};
/** The group of passages, the plan's own words (section 1). */
export const READ_LABEL = 'In what Cobrain read';
/**
 * The reader's own mail and chats Cobrain has not read yet (module 05 R62, F4): the plan's own words for the group at the
 * foot of the panel and of /search, folded to a count and one press, because such a message has not been routed to the
 * personal store and can be a family or a solicitor's mail.
 */
export const NOT_YET_READ_LABEL = 'In your mail Cobrain has not read yet';
/** What the fold says under the lines it opens: why they open nothing, and that they are the reader's alone. */
export const NOT_YET_READ_NOTE = 'Only you can see these. Cobrain has not read them yet, so they open nothing here.';
/** The tab of passages on /search, in the address as ?tab=read. */
export const READ_TAB = 'read';
export type Tab = SearchKind | typeof READ_TAB;

/** The box opens its panel from two characters (the plan, section 1), as the API asks nothing under two. */
export const MIN_CHARS = 2;
/** The names are asked 200 ms after the last key, the Deals find's own timing. */
export const RECORDS_WAIT_MS = 200;
/**
 * What Cobrain read is asked once the names have answered and the words have stood still this long more. It is the ask
 * bar's retrieval, about 2.7 s on the live store (B1951), so it is not asked at every pause while typing.
 */
export const CORPUS_WAIT_MS = 600;
/** Lines a kind shows in the panel. */
export const PANEL_EACH = 5;
/** Lines a kind shows on /search, the API's ceiling. */
export const PAGE_EACH = 50;

/** The kinds a store is asked for: everything in the commercial store, and in the personal one what a page can open. */
export function kindsFor(store: Store): readonly SearchKind[] {
  return store.kind === 'commercial' ? SEARCH_KINDS : PERSONAL_KINDS;
}
// BUGS.md D74: people too, since People lists the personal store with Personal on and a person's page reads the store
// its address names.
export const PERSONAL_KINDS: readonly SearchKind[] = ['people', 'tasks'];

export interface Hit extends SearchHit { kind: SearchKind; store: Store }
export interface Group { kind: SearchKind; count: number; hits: Hit[] }
export interface Found extends Passage { store: Store }
export interface Refused { subject: string; answer: string }

/** Several lists taken in turn, one from each, so neither store's lines are all pushed below the other's. */
export function interleave<T>(lists: readonly (readonly T[])[]): T[] {
  const out: T[] = [];
  const longest = Math.max(0, ...lists.map((l) => l.length));
  for (let i = 0; i < longest; i++) for (const l of lists) if (i < l.length) out.push(l[i]!);
  return out;
}

/** Each kind in the panel's order, its count the stores' sum and its lines taken in turn from each, `each` at most. */
export function mergeGroups(answers: readonly { store: Store; groups: readonly SearchGroup[] }[], each: number): Group[] {
  return SEARCH_KINDS.map((kind) => {
    const per = answers.map((a) => (a.groups.find((g) => g.kind === kind)?.hits ?? []).map((h): Hit => ({ ...h, kind, store: a.store })));
    const count = answers.reduce((n, a) => n + (a.groups.find((g) => g.kind === kind)?.count ?? 0), 0);
    return { kind, count, hits: interleave(per).slice(0, each) };
  });
}

/** The names, asked of each store for its kinds; a never-said subject (R44) is refused for both, as the words are. */
export async function askRecords(stores: readonly Store[], q: string, each: number, signal?: AbortSignal): Promise<{ refused: Refused | null; groups: Group[] }> {
  const answers = await Promise.all(stores.map((s) => search.records(s.tenantId, q, { kinds: kindsFor(s), each, ...(signal ? { signal } : {}) })));
  const refusal = answers.find((a) => a.refused);
  if (refusal) return { refused: { subject: refusal.refused!, answer: refusal.answer ?? '' }, groups: mergeGroups([], each) };
  return { refused: null, groups: mergeGroups(answers.map((a, i) => ({ store: stores[i]!, groups: a.groups })), each) };
}

/** What Cobrain read, asked of each store; the first store's refusal stands for the words. */
export async function askCorpus(stores: readonly Store[], q: string, signal?: AbortSignal): Promise<{ refused: Refused | null; passages: Found[] }> {
  const answers = await Promise.all(stores.map((s) => search.corpus(s.tenantId, q, signal)));
  const refusal = answers.find((a) => a.refused !== undefined);
  if (refusal && refusal.refused !== undefined) return { refused: { subject: refusal.refused, answer: refusal.answer }, passages: [] };
  return { refused: null, passages: interleave(answers.map((a, i) => (a.refused === undefined ? a.passages : []).map((p): Found => ({ ...p, store: stores[i]! })))) };
}

export interface Unread extends NotYetReadPassage { store: Store }

/**
 * The reader's own mail Cobrain has not read yet, asked of each store: the counts summed and the lines taken in turn. A
 * refused query counts nothing here; the corpus half says why. An answer without a count (an older server) is none.
 */
export async function askNotYetRead(stores: readonly Store[], q: string, each: number, signal?: AbortSignal): Promise<{ count: number; passages: Unread[] }> {
  const answers = await Promise.all(stores.map((s) => search.notYetRead(s.tenantId, q, { each, ...(signal ? { signal } : {}) })));
  const live = answers.map((a, i) => ({ a, store: stores[i]! })).filter(({ a }) => !a.refused && typeof a.count === 'number');
  return {
    count: live.reduce((n, { a }) => n + a.count, 0),
    passages: interleave(live.map(({ a, store }) => (a.passages ?? []).map((p): Unread => ({ ...p, store })))).slice(0, each),
  };
}

/** The page a name opens. */
export function hitPath(h: Hit): string {
  const id = encodeURIComponent(h.id);
  switch (h.kind) {
    case 'companies': return `/companies/${id}`;
    case 'people': return h.store.kind === 'personal' ? `/people/${id}?store=${encodeURIComponent(h.store.tenantId)}` : `/people/${id}`;
    case 'deals': return `/deals/${id}`;
    case 'meetings': return `/meetings/${id}`;
    case 'tasks': return `/tasks/${id}`;
    case 'chats': return `/chats/${id}`;
  }
}

/**
 * The page a passage opens: its triage item at the message (the item page draws the message ?signal= names above the
 * evidence), else the meeting, else the chat. The Meetings and Chats pages read the commercial store, so a personal
 * passage opens only an item. A message held by none of these opens nothing; its words are drawn where it is found.
 */
export function passagePath(p: Found): string | null {
  if (!p.opens) return null;
  if (p.opens.page === 'item') return `/items/${encodeURIComponent(p.opens.id)}?store=${encodeURIComponent(p.store.tenantId)}&signal=${encodeURIComponent(p.signalId)}#evidence`;
  if (p.store.kind !== 'commercial') return null;
  return p.opens.page === 'meeting' ? `/meetings/${encodeURIComponent(p.opens.id)}` : `/chats/${encodeURIComponent(p.opens.id)}`;
}

/** /search for these words with the reader's own mail Cobrain has not read already unfolded at its foot. */
export function unreadPath(q: string): string {
  return `${searchPath(q)}&unread=open`;
}

/** /search for these words, on one tab when named. */
export function searchPath(q: string, tab?: Tab): string {
  return `/search?q=${encodeURIComponent(q)}${tab ? `&tab=${tab}` : ''}`;
}

const TASK_STATUS: Record<string, string> = { suggested: 'Suggested', delegated: 'Handed on', parked: 'Parked', scheduled: 'Scheduled', in_progress: 'In progress', done: 'Done', dismissed: 'Dismissed' };

/** The line under a name: the API's detail, a task's state in words, and the date where a kind has one that means something. */
export function hitDetail(h: Hit): string | null {
  const parts: string[] = [];
  if (h.kind === 'tasks') { const s = h.detail ? TASK_STATUS[h.detail] : undefined; if (s) parts.push(s); }
  else if (h.detail) parts.push(h.detail);
  if (h.when && h.kind === 'tasks') parts.push(`due ${formatDate(h.when)}`);
  if (h.when && (h.kind === 'meetings' || h.kind === 'chats')) parts.push(formatDate(h.when));
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** corpus.signal_kind in words, for a passage with no subject. */
const KIND_WORD: Record<string, string> = {
  email: 'A mail', teams_chat: 'A Teams chat', teams_channel: 'A Teams channel post', cobrain_chat: 'A Cobrain chat', whatsapp: 'A WhatsApp message',
  call: 'A call', meeting: 'A meeting', calendar_event: 'A calendar event', document: 'A document', note: 'A note', news_item: 'A news item',
  digest: 'A digest', manual_note: 'A note',
};

/** What a passage is called: its subject, else what kind of thing it is. */
export function passageTitle(p: Pick<Found, 'subject' | 'kind'>): string {
  return p.subject?.trim() || KIND_WORD[p.kind] || 'An item';
}

/** Who, when and under what: the line under a passage's title. A message Cobrain has not read is filed under nothing. */
export function passageMeta(p: Pick<Found, 'participants' | 'occurredAt'> & { matters?: string | null }): string {
  return [p.participants, formatDate(p.occurredAt), p.matters ?? null].filter((x): x is string => !!x && x.trim() !== '').join(' · ');
}

/** The words a person can read when a search fails: the API's own sentence where it gave one. */
export function failureWords(e: unknown): string {
  return e instanceof Error && e.message ? e.message : 'the server did not answer';
}
